import dayjs from 'dayjs';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { InfoBlock } from 'app/components/commons/InfoBlock/InfoBlock';
import type {
  InfoCategory,
  InfoRow,
  InfoSection,
} from 'app/components/commons/InfoBlock/InfoBlock';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import type { BookingDetails } from 'app/typings/core/admin/bookings/booking';
import {
  getExtraRows,
  getFreeIncludedPerNightRow,
  getIncludedPerOnceRows,
  getPaidIncludedPerNightRows,
  getRoomRows,
  mapPriceSubRows,
} from 'app/utils/core/bookingDetails/utils';

type Props = {
  bookingDetails: BookingDetails;
};

export const PackageDetails = ({ bookingDetails }: Props) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { data: bedCategories } = useBedCategories();

  const payment = bookingDetails.payments[0];
  const isMultiNightBooking =
    dayjs(bookingDetails.checkOut)
      .startOf('day')
      .diff(dayjs(bookingDetails.checkIn).startOf('day'), 'day') > 1;

  const freePerNightRow = getFreeIncludedPerNightRow(bookingDetails);
  const paidPerNightRows = getPaidIncludedPerNightRows(
    bookingDetails,
    currencyFormatter
  );
  const perOnceRows = getIncludedPerOnceRows(bookingDetails, currencyFormatter);
  const extraRows = getExtraRows(bookingDetails, currencyFormatter);
  const roomRows = getRoomRows(bookingDetails, bedCategories ?? []);

  const perNightRows: InfoRow[] = [
    ...(freePerNightRow
      ? [
          {
            label: freePerNightRow.label,
            value: currencyFormatter(freePerNightRow.price),
            bold: false,
          },
        ]
      : []),
    ...paidPerNightRows.map(({ label, price, subRows }) => ({
      label,
      value: currencyFormatter(price),
      bold: false,
      subRows: subRows
        ? mapPriceSubRows(subRows, currencyFormatter)
        : undefined,
    })),
  ];
  const perOnceInfoRows: InfoRow[] = perOnceRows.map(
    ({ label, price, subRows }) => ({
      label,
      value: currencyFormatter(price),
      bold: false,
      subRows: subRows
        ? mapPriceSubRows(subRows, currencyFormatter)
        : undefined,
    })
  );

  // Labels only make sense for multi-night bookings; otherwise keep a single
  // category so per-night and per-once rows share the same spacing.
  const includedCategories: InfoCategory[] = (
    isMultiNightBooking
      ? [
          { label: 'Included every night', rows: perNightRows },
          { label: 'Included once', rows: perOnceInfoRows },
        ]
      : [{ rows: [...perNightRows, ...perOnceInfoRows] }]
  ).filter(({ rows }) => rows.length > 0);

  const sections: InfoSection[] = [
    ...(includedCategories.length > 0
      ? [
          {
            title: 'Included activities',
            categories: includedCategories,
          },
        ]
      : []),
    ...(extraRows.length > 0
      ? [
          {
            title: 'Extras',
            rows: extraRows.map(
              ({
                label,
                unitPrice,
                price,
                subRows,
                showUnitPrice,
                isOriginals,
              }) => ({
                label: showUnitPrice
                  ? `${label} (${
                      isOriginals ? 'Originals • ' : ''
                    }${currencyFormatter(unitPrice)})`
                  : label,
                value: currencyFormatter(price),
                bold: false,
                subRows: subRows
                  ? mapPriceSubRows(subRows, currencyFormatter)
                  : undefined,
              })
            ),
          },
        ]
      : []),
    ...(roomRows.length > 0
      ? [
          {
            title: 'Room',
            rows: roomRows.map(({ label, price, subRows }) => ({
              label,
              value: currencyFormatter(price),
              bold: false,
              subRows: subRows
                ? mapPriceSubRows(subRows, currencyFormatter)
                : undefined,
            })),
          },
        ]
      : []),
    {
      rows: [
        {
          label: 'Staycation fees',
          value: currencyFormatter(payment?.feesAmount ?? 0),
        },
      ],
    },
    {
      rows: [
        {
          label: 'Total paid incl. VAT',
          value: currencyFormatter(payment?.totalAmount ?? 0),
          bold: true,
        },
        {
          label: 'Net commission amount excl.',
          value: currencyFormatter(payment?.commissionAmount ?? 0),
        },
      ],
    },
  ];

  return <InfoBlock title="Package details" sections={sections} />;
};
